import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeftIcon,
  ExclamationIcon,
  PhoneIcon,
  MailIcon,
  DocumentTextIcon,
  CalendarIcon,
  LocationMarkerIcon,
  CurrencyDollarIcon,
  TagIcon,
  ClockIcon,
  UserIcon,
  OfficeBuildingIcon,
  CheckCircleIcon,
  StarIcon
} from '@heroicons/react/outline';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import ImageGallery from '../components/ImageGallery';

const RFQDetails = () => {
  const { rfqId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [rfq, setRfq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadRFQDetails = async () => {
      try {
        setLoading(true);
        
        // If we have the RFQ data from navigation state, use it
        if (location.state?.rfq) {
          setRfq(location.state.rfq);
        } else {
          // Otherwise fetch from Firebase
          const rfqDoc = await getDoc(doc(db, 'posts', rfqId));
          if (rfqDoc.exists()) {
            setRfq({ id: rfqDoc.id, ...rfqDoc.data() });
          } else {
            setError('RFQ not found');
          }
        }
      } catch (err) {
        console.error('Error loading RFQ details:', err);
        setError('Failed to load RFQ details');
      } finally {
        setLoading(false);
      }
    };

    loadRFQDetails();
  }, [rfqId, location.state]);

  const handleSubmitQuote = () => {
    if (!user) {
      // For unauthenticated users, navigate directly to quote form
      // The form will handle authentication when they try to submit
      navigate(`/submit-quote/${rfqId}`, { state: { rfq } });
      return;
    }

    // Navigate to submit quote page
    navigate(`/submit-quote/${rfqId}`, { state: { rfq } });
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyMap = {
      'high': { 
        color: 'bg-gradient-to-r from-red-500 to-red-600', 
        textColor: 'text-white',
        icon: 'bg-red-400',
        label: 'High Priority' 
      },
      'medium': { 
        color: 'bg-gradient-to-r from-yellow-500 to-orange-500', 
        textColor: 'text-white',
        icon: 'bg-yellow-400',
        label: 'Medium Priority' 
      },
      'low': { 
        color: 'bg-gradient-to-r from-green-500 to-emerald-500', 
        textColor: 'text-white',
        icon: 'bg-green-400',
        label: 'Low Priority' 
      }
    };
    
    const config = urgencyMap[urgency] || urgencyMap['medium'];
    
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${config.color} ${config.textColor} shadow-sm`}>
        <ExclamationIcon className="h-4 w-4 mr-2" />
        {config.label}
      </span>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date not specified';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatBudget = (budget) => {
    if (!budget) return 'Not specified';
    
    // If budget is a number, format it directly
    if (typeof budget === 'number') {
      return `₦${budget.toLocaleString()}`;
    }
    
    // If budget is an object, try to extract the value
    if (typeof budget === 'object') {
      // Check if it has a 'value' property
      if (budget.value !== undefined) {
        return `₦${Number(budget.value).toLocaleString()}`;
      }
      // Check if it has an 'amount' property
      if (budget.amount !== undefined) {
        return `₦${Number(budget.amount).toLocaleString()}`;
      }
      // If it's a Firestore timestamp or other object, try to convert to string
      return `₦${String(budget)}`;
    }
    
    // If budget is a string, try to convert to number
    if (typeof budget === 'string') {
      const numBudget = parseFloat(budget);
      if (!isNaN(numBudget)) {
        return `₦${numBudget.toLocaleString()}`;
      }
      return budget; // Return as is if it's not a valid number
    }
    
    return 'Not specified';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading RFQ details...</p>
        </div>
      </div>
    );
  }

  if (error || !rfq) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <ExclamationIcon className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">RFQ Not Found</h2>
            <p className="text-gray-600 mb-6">{error || 'The RFQ you are looking for does not exist or has been removed.'}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => {
                  if (user) {
                    navigate('/market-feed');
                  } else {
                    navigate('/');
                  }
                }}
                className="px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                {user ? 'Back to Market Feed' : 'Back to Home'}
              </button>
              {!user && (
                <button
                  onClick={() => navigate('/signin')}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all duration-300 font-medium"
                >
                  Sign In
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Enhanced Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => {
                  if (user) {
                    navigate('/market-feed');
                  } else {
                    navigate('/');
                  }
                }}
                className="mr-4 p-3 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50 transition-all duration-300 group"
              >
                <ArrowLeftIcon className="h-6 w-6 group-hover:transform group-hover:-translate-x-1 transition-transform" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  RFQ Details
                </h1>
                <p className="text-gray-500 text-sm mt-1">Request for Quotation</p>
              </div>
            </div>
            
            {user && (
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate(`/quotes/${rfqId}`, { state: { rfq } })}
                  className="px-6 py-3 text-gray-600 hover:text-gray-900 transition-all duration-300 font-medium hover:bg-gray-50 rounded-xl"
                >
                  View Quotes ({rfq.replyCount || 0})
                </button>
                <button
                  onClick={handleSubmitQuote}
                  className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg"
                >
                  Submit Quote
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8 pb-32">
        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          {/* Enhanced RFQ Header */}
          <div className="bg-gradient-to-r from-orange-50 to-red-50 p-8 border-b border-gray-100">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <h2 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
                  {rfq.textContent || rfq.title || 'RFQ Title'}
                </h2>
                <div className="flex items-center space-x-4 mb-6">
                  {getUrgencyBadge(rfq.urgency)}
                  <span className="text-gray-400">•</span>
                  <div className="flex items-center text-gray-600">
                    <UserIcon className="h-4 w-4 mr-2" />
                    <span className="font-medium">{rfq.authorName || rfq.userName || 'Anonymous'}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Key Information Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-2">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-500 mr-2" />
                  <p className="text-sm font-medium text-gray-500">Budget</p>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatBudget(rfq.budget)}
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-2">
                  <CalendarIcon className="h-5 w-5 text-blue-500 mr-2" />
                  <p className="text-sm font-medium text-gray-500">Posted</p>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(rfq.timestamp || rfq.createdAt)}
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-2">
                  <LocationMarkerIcon className="h-5 w-5 text-red-500 mr-2" />
                  <p className="text-sm font-medium text-gray-500">Location</p>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {rfq.location?.name || rfq.location || 'Not specified'}
                </p>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center mb-2">
                  <TagIcon className="h-5 w-5 text-purple-500 mr-2" />
                  <p className="text-sm font-medium text-gray-500">Category</p>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {(rfq.categories || [rfq.channel || rfq.category || 'General']).join(', ')}
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced Description */}
          <div className="p-8 border-b border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                <DocumentTextIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Description</h3>
            </div>
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 leading-relaxed text-lg">
                {rfq.textContent || rfq.description || 'No detailed description available for this RFQ.'}
              </p>
            </div>
          </div>

          {/* Enhanced Images */}
          {rfq.media && rfq.media.length > 0 && (
            <div className="p-8 border-b border-gray-100">
              <div className="flex items-center mb-6">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg flex items-center justify-center mr-3">
                  <DocumentTextIcon className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Images & Attachments</h3>
              </div>
              <div className="bg-gray-50 rounded-xl p-6">
                <ImageGallery images={rfq.media} />
              </div>
            </div>
          )}

          {/* Enhanced Supplier Information */}
          <div className="p-8 border-b border-gray-100">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center mr-3">
                <OfficeBuildingIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Posted By</h3>
            </div>
            <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mr-4 shadow-lg">
                  <span className="text-white font-bold text-xl">
                    {rfq.authorName?.charAt(0) || rfq.userName?.charAt(0) || 'U'}
                  </span>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-1">
                    {rfq.authorName || rfq.userName || 'Anonymous'}
                  </h4>
                  <p className="text-gray-600 font-medium">
                    {rfq.authorCompany || 'Company not specified'}
                  </p>
                </div>
              </div>

              {(rfq.authorEmail || rfq.authorPhone) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {rfq.authorEmail && (
                    <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                      <MailIcon className="h-5 w-5 text-blue-500 mr-3" />
                      <span className="text-gray-700 font-medium">{rfq.authorEmail}</span>
                    </div>
                  )}
                  {rfq.authorPhone && (
                    <div className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                      <PhoneIcon className="h-5 w-5 text-green-500 mr-3" />
                      <span className="text-gray-700 font-medium">{rfq.authorPhone}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Additional Details */}
          <div className="p-8">
            <div className="flex items-center mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                <CheckCircleIcon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Additional Information</h3>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
                  <h4 className="text-lg font-bold text-gray-900">Timeline</h4>
                </div>
                <p className="text-gray-700 font-medium">
                  {rfq.timeline || 'Timeline not specified'}
                </p>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6">
                <div className="flex items-center mb-3">
                  <StarIcon className="h-5 w-5 text-green-500 mr-2" />
                  <h4 className="text-lg font-bold text-gray-900">Requirements</h4>
                </div>
                <p className="text-gray-700 font-medium">
                  {rfq.requirements || 'Requirements not specified'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Action Buttons */}
        {user && (
          <div className="mt-8 flex justify-center space-x-6">
            <button
              onClick={() => navigate(`/quotes/${rfqId}`, { state: { rfq } })}
              className="px-8 py-4 text-gray-600 hover:text-gray-900 transition-all duration-300 font-semibold bg-white rounded-xl shadow-lg hover:shadow-xl border border-gray-200 hover:border-gray-300"
            >
              View Quotes ({rfq.replyCount || 0})
            </button>
            <button
              onClick={handleSubmitQuote}
              className="px-10 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Submit Quote
            </button>
          </div>
        )}
      </div>

      {/* Sticky Submit Quote Button - Always Visible */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="hidden sm:block">
                <h4 className="text-lg font-bold text-gray-900">Ready to submit your quote?</h4>
                <p className="text-sm text-gray-600">Get started with your professional quotation</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user && (
                <button
                  onClick={() => navigate(`/quotes/${rfqId}`, { state: { rfq } })}
                  className="px-6 py-3 text-gray-600 hover:text-gray-900 transition-all duration-300 font-medium bg-gray-100 hover:bg-gray-200 rounded-xl"
                >
                  View Quotes ({rfq.replyCount || 0})
                </button>
              )}
              <button
                onClick={handleSubmitQuote}
                className="px-8 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl hover:from-orange-600 hover:to-red-600 transition-all duration-300 font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-1 text-lg"
              >
                {user ? 'Submit Quote' : 'Submit Quote Now'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFQDetails; 